import { Button, CircularProgress, Grid } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { useLazyQuery } from '@parkchirp/api-client';
import type { Dayjs } from 'dayjs';
import { dayjs } from 'parkchirp-shared/utils';
import React, { useState } from 'react';

import { GET_DAILY_REVENUE_REPORT_CSV } from '../../queries';

export const DailyRevenueReportForm = ({
	facilityId,
	setShowSnackbar,
}: {
	facilityId: string;
	setShowSnackbar: any;
}) => {
	const [downloading, setDownloading] = useState<boolean>(false);
	const [startDate, setStartDate] = useState<string>(null);
	const [endDate, setEndDate] = useState<string>(null);

	const [getDailyRevenueReport] = useLazyQuery(GET_DAILY_REVENUE_REPORT_CSV);

	const handleDownload = async () => {
		if (!startDate) {
			setShowSnackbar({
				message: 'Please provide a Start Date',
				severity: 'error',
			});
			return;
		}

		if (!endDate) {
			setShowSnackbar({
				message: 'Please provide an End Date',
				severity: 'error',
			});
			return;
		}

		if (dayjs(startDate) > dayjs(endDate)) {
			setShowSnackbar({
				message: 'Start Date must be before End Date',
				severity: 'error',
			});
			return;
		}

		setDownloading(true);
		await getDailyRevenueReport({
			variables: {
				facilityId,
				startDate: dayjs(startDate).format('YYYY-MM-DD'),
				endDate: dayjs(endDate).format('YYYY-MM-DD'),
			},
			fetchPolicy: 'network-only',
			onCompleted: (response) => {
				setDownloading(false);
				window.open(response.getDailyRevenueReportCsv.url);
			},
			onError: (error) => {
				setDownloading(false);
				setShowSnackbar({
					message: error.message,
					severity: 'error',
				});
			},
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
		});
	};

	return (
		<div style={{ marginTop: '20px' }}>
			<Grid item xs={4} sx={{ marginBottom: '15px', width: '250px' }}>
				<h4 style={{ marginBottom: '10px' }}>Report Start Date:</h4>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DatePicker<Dayjs>
						label='Start Date'
						value={startDate ? dayjs(startDate) : null}
						slotProps={{
							textField: {
								fullWidth: true,
							},
						}}
						onChange={(date) => setStartDate(date?.format('YYYY-MM-DD') ?? null)}
					/>
				</LocalizationProvider>
			</Grid>
			<Grid item xs={4} sx={{ marginBottom: '15px', width: '250px' }}>
				<h4 style={{ marginBottom: '10px' }}>Report End Date:</h4>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DatePicker<Dayjs>
						label='End Date'
						value={endDate ? dayjs(endDate) : null}
						slotProps={{
							textField: {
								fullWidth: true,
							},
						}}
						onChange={(date) => setEndDate(date?.format('YYYY-MM-DD') ?? null)}
					/>
				</LocalizationProvider>
			</Grid>
			<Grid item sx={{ marginTop: '25px' }}>
				<Button variant='contained' size='small' onClick={handleDownload} disabled={downloading}>
					{downloading ? <CircularProgress size={24} /> : 'Download Report'}
				</Button>
			</Grid>
		</div>
	);
};
